import React, { useState, useEffect } from 'react';
import { Plus, Lightbulb as LightbulbIcon, Edit2, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import ConfirmDialog from '../components/ConfirmDialog';

const LessonsPage = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [filter, setFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  useEffect(() => {
    fetchLessons();
  }, []);
  
  const fetchLessons = async () => {
    try {
      const data = await apiFetch('/api/lessons');
      setLessons(data.lessons || []);
    } catch (error) {
      console.error('Error fetching lessons:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const performDeleteLesson = async () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    try {
      await apiFetch(`/api/lessons/${id}`, { method: 'DELETE' });
      toast.success('Lesson deleted');
      fetchLessons();
    } catch (error) {
      console.error('Error deleting lesson:', error);
      toast.error(error?.message || 'Failed to delete lesson');
      throw error;
    }
  };
  
  const toggleApplied = async (lessonId, currentStatus) => {
    try {
      await apiFetch(`/api/lessons/${lessonId}?applied=${!currentStatus}`, {
        method: 'PUT',
      });
      fetchLessons();
      toast.success(!currentStatus ? 'Marked as applied!' : 'Unmarked');
    } catch (error) {
      console.error('Error updating lesson:', error);
      toast.error(error?.message || 'Could not update lesson');
    }
  };
  
  const categoryColors = {
    people: 'teal',
    money: 'gold',
    product: 'lavender',
    process: 'amber',
    mental: 'sage',
    other: 'text-tertiary'
  };
  
  const filteredLessons = lessons.filter(lesson => {
    if (filter === 'all') return true;
    if (filter === 'applied') return lesson.applied;
    return lesson.category === filter;
  });
  
  return (
    <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="lessons-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl font-bold text-text-primary mb-2">Lessons Library</h1>
            <p className="text-text-secondary">Your wisdom, captured</p>
          </div>
          <button
            onClick={() => { setEditingLesson(null); setShowModal(true); }}
            className="flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all shadow-sm"
            data-testid="new-lesson-button"
          >
            <Plus size={20} />
            Add Lesson
          </button>
        </div>
        
        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
              filter === 'all' ? 'bg-gold text-white' : 'bg-bg-surface text-text-secondary border border-border hover:border-gold'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('applied')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
              filter === 'applied' ? 'bg-sage text-white' : 'bg-bg-surface text-text-secondary border border-border hover:border-gold'
            }`}
          >
            ✓ Applied
          </button>
          {Object.keys(categoryColors).map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all capitalize ${
                filter === cat ? 'bg-gold text-white' : 'bg-bg-surface text-text-secondary border border-border hover:border-gold'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4"></div>
          </div>
        ) : filteredLessons.length === 0 ? (
          <div className="text-center py-12 bg-bg-surface rounded-2xl border border-border">
            <div className="w-16 h-16 bg-amber-light rounded-full flex items-center justify-center mx-auto mb-4">
              <LightbulbIcon className="text-amber" size={32} />
            </div>
            <h3 className="font-display text-xl font-bold text-text-primary mb-2">No lessons yet</h3>
            <p className="text-text-secondary mb-6">Start capturing your learnings</p>
            <button
              onClick={() => { setEditingLesson(null); setShowModal(true); }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all"
            >
              <Plus size={20} />
              Add First Lesson
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredLessons.map((lesson) => {
              const color = categoryColors[lesson.category];
              
              return (
                <div key={lesson.id} className="bg-bg-surface rounded-2xl p-6 border border-border card-hover">
                  <div className="flex items-start gap-4">
                    <button
                      onClick={() => toggleApplied(lesson.id, lesson.applied)}
                      className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        lesson.applied
                          ? 'bg-sage border-sage'
                          : 'border-border hover:border-sage'
                      }`}
                    >
                      {lesson.applied && <CheckCircle size={16} className="text-white" />}
                    </button>
                    
                    <div className="flex-1">
                      <p className={`text-base text-text-primary mb-3 ${lesson.applied ? 'opacity-60' : ''}`}>
                        {lesson.lesson_text}
                      </p>
                      
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 bg-${color}-light text-${color} text-xs font-medium rounded-full capitalize`}>
                          {lesson.category}
                        </span>
                        <span className="text-xs text-text-tertiary">
                          {new Date(lesson.date_learned).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingLesson(lesson); setShowModal(true); }}
                        className="p-2 text-text-secondary hover:text-gold hover:bg-gold-light rounded-lg transition-all"
                        title="Edit lesson"
                        data-testid={`edit-lesson-${lesson.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() =>
                          setDeleteTarget({
                            id: lesson.id,
                            preview: (lesson.lesson_text || '').slice(0, 220),
                          })
                        }
                        className="p-2 text-text-secondary hover:text-error hover:bg-error/10 rounded-lg transition-all"
                        title="Delete lesson"
                        data-testid={`delete-lesson-${lesson.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {showModal && (
          <LessonModal
            lesson={editingLesson}
            onClose={() => { setShowModal(false); setEditingLesson(null); }}
            onSave={fetchLessons}
          />
        )}

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title="Delete this lesson?"
          message="This will permanently remove it from your library. You can't undo this."
          detail={deleteTarget?.preview ? `"${deleteTarget.preview}${deleteTarget.preview.length >= 220 ? '…' : ''}"` : null}
          confirmLabel="Delete lesson"
          onConfirm={performDeleteLesson}
        />
      </div>
    </div>
  );
};

const LessonModal = ({ lesson, onClose, onSave }) => {
  const isEditing = !!lesson;
  const [formData, setFormData] = useState(lesson ? {
    lesson_text: lesson.lesson_text,
    category: lesson.category,
    date_learned: lesson.date_learned
  } : {
    lesson_text: '',
    category: 'other',
    date_learned: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const path = isEditing ? `/api/lessons/${lesson.id}` : '/api/lessons';
      await apiFetch(path, {
        method: isEditing ? 'PUT' : 'POST',
        body: JSON.stringify(formData),
      });
      toast.success(isEditing ? 'Lesson updated!' : 'Lesson added!');
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving lesson:', error);
      toast.error(error?.message || 'Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-overlay-in" onClick={onClose}>
      <div className="bg-bg-surface rounded-2xl p-8 max-w-2xl w-full animate-modal-in" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-3xl font-bold text-text-primary mb-6">
          {isEditing ? 'Edit Lesson' : 'Add Lesson'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Lesson Text *</label>
            <textarea
              value={formData.lesson_text}
              onChange={(e) => setFormData({ ...formData, lesson_text: e.target.value })}
              required
              rows={4}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none"
              placeholder="What did you learn?"
              data-testid="lesson-text-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="people">People</option>
                <option value="money">Money</option>
                <option value="product">Product</option>
                <option value="process">Process</option>
                <option value="mental">Mental</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Date Learned</label>
              <input
                type="date"
                value={formData.date_learned}
                onChange={(e) => setFormData({ ...formData, date_learned: e.target.value })}
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
          </div>
          
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-border text-text-secondary rounded-xl hover:border-gold transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.lesson_text}
              className="flex-1 py-3 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary/30 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all"
              data-testid="save-lesson-button"
            >
              {saving ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Lesson' : 'Add Lesson')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LessonsPage;
